/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react'
import { connect } from 'react-redux'
import { Accordion } from 'semantic-ui-react'
import filter from 'lodash/filter'
// Local
import {
  CONDITION,
  HCC_GROUP,
  NOTE,
  ROLES
} from '../../../constant'
import {
  condition as conditionUtils,
  conditionGroupTitle,
  filterNlpConditions,
  filterWithStatus,
  groupByAddressed,
  hasConditionsOrActivities,
  orderConditionsByHccConfidenceDescending
} from '../../common/conditionUtils'
import {
  encounterConditionGroups,
  encounterConditionGroupsEnabled,
  hccGroupingEnabled
} from '../../common/currentUserUtils'
import { groupByHccCode } from '../../common/hccUtils'
import * as lockUtils from '../../common/lockUtils'
import ConditionHeader from '../common/conditionHeader'
import ConditionCreate from '../common/conditionCreate'
import CdiConditionEdit from './cdiConditionEdit'
import CdiConditionHccGroup from './cdiConditionHccGroup'
import CdiConditionRead from './cdiConditionRead'
import If from '../../common/if'
import {
  EncounterConditionDisplay,
  EncounterLock
} from '../../authenticated/types'
import { ObjectValues } from '../../../types/types'
import { RootState } from '../../../store'
// Styling
import '../detail.css'

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

/**
 * Provides a component that renders the CDI encounter conditions and condition
 * editors in the Version 2 display format.
 *
 * The CDI version 2 display format is very similar to the Physician UI version
 * display format. Conditions are grouped under HCC code, sorting unaddressed
 * HCCs to the top
 *
 * @param onHccGroupAction Callback for HCC group action.
 *        Arguments: action, hcc-group-id
 */

type Props = ReduxStateProps & {
  activeConditionId: string | null
  onConditionAction: (
    action: keyof typeof CONDITION.ACTION,
    condition: EncounterConditionDisplay[] | EncounterConditionDisplay | string
  ) => Promise<void> | void
  onConditionNoteAction: (
    action: ObjectValues<typeof NOTE.ACTION>,
    condition: EncounterConditionDisplay,
    noteContent: string,
    reason: ObjectValues<typeof NOTE.REASON>
  ) => void
  activeHccGroupId: string | null
  onHccGroupAction: (
    action: keyof typeof HCC_GROUP.ACTION,
    hccGroupID: string
  ) => void
  encounterDetail: any
  conditionGroups: any
}

function CdiEncounterConditionsV2(
  props: Props
): JSX.Element {
  const {
    activeConditionId,
    onConditionNoteAction,
    onConditionAction,
    currentUser,
    activeHccGroupId,
    onHccGroupAction,
    encounterDetail,
    conditionGroups: conditionGroupsProp
  } = props

  const isActiveCondition = (
    condition: EncounterConditionDisplay
  ): boolean => (
    condition &&
    !!condition.id &&
    condition.id === activeConditionId
  )

  const onClickConditionTitle = (
    condition: EncounterConditionDisplay
  ): void => {
    if (isActiveCondition(condition)) {
      onConditionAction(
        CONDITION.ACTION.DEACTIVATE,
        condition
      )
    } else {
      onConditionAction(
        CONDITION.ACTION.ACTIVATE,
        condition
      )
    }
  }

  const onSubmitConditionNote = (
    condition: EncounterConditionDisplay,
    noteContent: string,
    noteReason: ObjectValues<typeof NOTE.REASON>
  ): void => {
    onConditionNoteAction(
      NOTE.ACTION.CREATE,
      condition,
      noteContent,
      noteReason
    )
  }

  const onCreateCondition = (
    condition: any
  ): Promise<void> => {
    return onConditionAction(
      CONDITION.ACTION.CREATE,
      condition
    ) as Promise<void>
  }

  const filterConditions = (
    conditions: EncounterConditionDisplay[],
    conditionStatusCode: string
  ) => {
    const nlpFiltered = filterNlpConditions(
      currentUser,
      conditions
    )
    const ignoreCodes = [
      CONDITION.CODE.REMOVED,
      CONDITION.CODE.DISMISSED
    ]
    return orderConditionsByHccConfidenceDescending(
      filterWithStatus(
        ROLES.CDI,
        nlpFiltered,
        conditionStatusCode,
        ignoreCodes
      )
    )
  }

  const groupConditions = (
    conditions: EncounterConditionDisplay[]
  ) => {
    if (hccGroupingEnabled(currentUser)) {
      return groupByHccCode(conditions)
    }
    return groupByAddressed(conditions)
  }

  const sortConditions = (
    conditions: EncounterConditionDisplay[]
  ): EncounterConditionDisplay[] => {
    const addressed = filter(
      conditions,
      condition => conditionUtils.isConditionAddressed(condition)
    )
    const notAddressed = filter(
      conditions,
      condition => !conditionUtils.isConditionAddressed(condition)
    )
    return notAddressed.concat(addressed)
  }

  const sortConditionGroups = (groups: any) => {
    const withHcc = groups.filter((g: any) => g.hccCode !== null)
    const withHccAddressed = withHcc.filter((g: any) => g.addressed === true)
    const withHccNotAddressed = withHcc.filter((g: any) => g.addressed === false)
    const withoutHcc = groups.filter((g: any) => g.hccCode === null)
    const withoutHccAddressed = withoutHcc.filter((g: any) => g.addressed === true)
    const withoutHccNotAddressed = withoutHcc.filter((g: any) => g.addressed === false)
    return withHccNotAddressed.concat(withoutHccNotAddressed).concat(withHccAddressed).concat(withoutHccAddressed)
  }

  const renderHccGroup = (
    group: any,
    index: any,
    groupTitle: any,
    editMode: any,
    encounterDate: number
  ) => (
    <CdiConditionHccGroup
      key={ `${group.hccCode}-${index}` }
      editMode={ editMode }
      hccCode={ group.hccCode }
      hccDescription={ group.hccDescription }
      activeHccGroupId={ activeHccGroupId }
      conditionGroupTitle={ groupTitle }
      conditions={ group.conditions }
      encounterDate={ encounterDate }
      onSubmitNote={ onSubmitConditionNote }
      onConditionAction={ onConditionAction }
      onHccGroupAction={ onHccGroupAction }
    />
  )

  const renderUngrouped = (
    group: any,
    index: number,
    editMode: boolean,
    encounterDate: number
  ) => group.conditions.map((
    condition: EncounterConditionDisplay,
    conditionIndex: number
  ) => editMode ? (
    <CdiConditionEdit
      key={ condition.id }
      currentUser={ currentUser }
      condition={ condition }
      encounterDate={ encounterDate }
      displayIndex={ conditionIndex }
      isActive={ isActiveCondition(condition) }
      onClickTitle={ onClickConditionTitle }
      onSubmitNote={ onSubmitConditionNote }
      onConditionAction={ onConditionAction }
      hccChild={ false }
    />
  ) : (
    <CdiConditionRead
      key={ condition.id }
      currentUser={ currentUser }
      condition={ condition }
      displayIndex={ conditionIndex }
      isActive={ isActiveCondition(condition) }
      onClickTitle={ onClickConditionTitle }
      onSubmitNote={ onSubmitConditionNote }
      hccChild={ false }
    />
  ))

  const renderConditions = (
    encounterDetailProp: any,
    displayConditions: any,
    editMode: boolean
  ) => {
    const key = encounterDetailProp.encounter.id
    const encounterDate = encounterDetailProp.encounter.start
    const patient = encounterDetailProp.encounter.patient
    return (
      <div
        className="cdi-encounter-conditions m-1-top"
        id="cdi-encounter-conditions"
        key={ key }
      >
        {
          displayConditions.map((
            display: any,
            displayIndex: number
          ) => {
            display.hasInsightsToShow = hasConditionsOrActivities(display.conditions, currentUser.type)
            return (
              <div key={ displayIndex } >
                <ConditionHeader
                  title={ display.title }
                  displayIndex={ displayIndex }
                  conditions={ [] }
                />
                { display.hasInsightsToShow &&
                  <Accordion styled fluid>
                    {
                      display.conditions.map((group: any, groupIndex: number) => {
                        if (group.hccCode === null && group.conditions.length > 0) {
                          return renderUngrouped(group, groupIndex, editMode, encounterDate)
                        } if (group.hccCode !== null && group.conditions && group.conditions.length > 0) {
                          return renderHccGroup(group, groupIndex, display.title, editMode, encounterDate)
                        }
                        return null

                      })
                    }
                  </Accordion>
                }
                { !display.hasInsightsToShow &&
                  <Accordion styled fluid>
                    <Accordion.Content>
                      <span>{ `No ${display.title} to show at this time.` }</span>
                    </Accordion.Content>
                  </Accordion>
                }
              </div>
            )
          })
        }
        <If isTrue={ editMode }>
          <div id="cdi-encounter-condition-create">
            <ConditionHeader
              title="Add Condition"
              conditions={ [] }
              displayIndex={ 0 }
            />
            <Accordion fluid styled>
              <ConditionCreate
                onSave={ onCreateCondition }
                patient= { patient }
              />
            </Accordion>
          </div>
        </If>
      </div>
    )
  }

  const { lock } = encounterDetail.encounter
  const conditionGroups = encounterConditionGroups(
    currentUser,
    conditionGroupsProp as string
  )
  const groupsEnabled = encounterConditionGroupsEnabled(currentUser)
  const editMode = lockUtils.canEdit(lock as EncounterLock, currentUser)
  const firstTouchedDate = encounterDetail.encounter.firstTouchedDate
  const visibleConditions = (encounterDetail.conditions as EncounterConditionDisplay[])
    .filter(c => !(c.clinicalEvidence || []).some((ce: any) =>
      ce.deletedBy != null &&
      ce.deletedDate != null &&
      firstTouchedDate != null &&
      new Date(ce.deletedDate) < new Date(firstTouchedDate as any)
    ))
  const displayConditions = []

  conditionGroups.forEach((g: string) => {
    if (groupsEnabled[g] === true) {
      displayConditions.push(
        {
          title: conditionGroupTitle(g as keyof typeof CONDITION.TYPE),
          conditions: sortConditionGroups(
            groupConditions(
              sortConditions(
                filterConditions(
                  visibleConditions,
                  g
                )
              )
            )
          )
        }
      )
    }
  })

  displayConditions.push(
    {
      title: 'Removed Conditions',
      conditions: groupByAddressed(
        orderConditionsByHccConfidenceDescending(
          filter(visibleConditions,
            c => c.conditionCode === CONDITION.CODE.REMOVED ||
              c.conditionCode === CONDITION.CODE.DISMISSED)
        )
      )
    }
  )

  return renderConditions(encounterDetail, displayConditions, editMode)
}

export default connect(mapStateToProps)(CdiEncounterConditionsV2)
